/**
 * Обвязка правки блока: плашки (конфликт, перенос правок, отказ сервера,
 * «пересчитано») и прилипающая полоса «Сохранить и пересчитать».
 */
import { pluralizeRu } from '@shared/lib/formatFile';
import { Button } from '@shared/ui/Button';
import { typo } from '@entities/fleet';
import { isEditableStatus, prcStatusLabel } from '@entities/prc';
import { Notice, TextButton } from '@widgets/FleetRegistry/ui';
import { CarriedNotice, ConflictNotice, RecalcNotice, SaveBar, Stack } from '../ui';
import type { BlockEditor } from './useBlockEditor';

export function EditorNotices({
  editor,
  lead,
  onCompare,
}: {
  editor: BlockEditor;
  /** «Цены сохранены». */
  lead: string;
  onCompare: () => void;
}) {
  return (
    <Stack>
      {editor.conflict && (
        <ConflictNotice
          message={editor.conflict.message}
          onReread={editor.reread}
          busy={editor.rereading}
          buttonRef={editor.rereadRef}
        />
      )}
      {editor.carried && (
        <CarriedNotice
          carried={editor.carried.carried}
          conflicts={editor.carried.conflicts}
          dropped={editor.carried.dropped}
        />
      )}
      {editor.serverError && (
        <Notice $tone="danger" role="alert">
          <span className="text">{editor.serverError}</span>
        </Notice>
      )}
      {editor.recalc && (
        <RecalcNotice
          summary={editor.recalc}
          lead={lead}
          onCompare={onCompare}
          onDismiss={editor.dismissRecalc}
        />
      )}
    </Stack>
  );
}

/** Почему блок только для чтения: статус версии или права. */
/**
 * Почему значения не правятся. Нет прав — об этом уже говорит плашка раздела
 * «Режим просмотра» над страницей; здесь — только про статус версии.
 */
export function ReadOnlyNote({ status, canWrite }: { status: string; canWrite: boolean }) {
  if (isEditableStatus(status) || !canWrite) return null;
  const text = `Версия — «${prcStatusLabel(status).toLowerCase()}»: значения только для просмотра. Чтобы поменять цены, создайте копию версии.`;
  return (
    <Stack>
      <Notice $tone="info" role="note">
        <span className="text">{typo(text)}</span>
      </Notice>
    </Stack>
  );
}

export function EditorSaveBar({ editor }: { editor: BlockEditor }) {
  const n = editor.dirtyCount;
  if (!editor.editable || (n === 0 && !editor.saving)) return null;
  const issues = editor.showIssues ? editor.issues.size : 0;
  return (
    <SaveBar role="region" aria-label="Сохранение правок">
      <p className="status" role="status">
        {editor.conflict ? (
          <>
            Версию изменили, пока шла правка ·{' '}
            <TextButton type="button" onClick={editor.reread}>
              перечитать
            </TextButton>
          </>
        ) : issues > 0 ? (
          <>
            Проверьте {issues} {pluralizeRu(issues, ['значение', 'значения', 'значений'])} ·{' '}
            <TextButton type="button" onClick={editor.focusFirstIssue}>
              к первому
            </TextButton>
          </>
        ) : (
          <>
            <strong>
              Изменено {n} {pluralizeRu(n, ['значение', 'значения', 'значений'])}
            </strong>{' '}
            {typo('— сохранение сразу пересчитает все модели версии')}
          </>
        )}
      </p>
      <div className="buttons">
        <Button type="button" $variant="ghost" disabled={editor.saving} onClick={editor.reset}>
          Отменить правки
        </Button>
        <Button
          type="button"
          $variant="primary"
          disabled={editor.saving || editor.conflict !== null}
          onClick={() => void editor.submit()}
        >
          {editor.saving ? 'Сохраняю и пересчитываю…' : 'Сохранить и пересчитать'}
        </Button>
      </div>
    </SaveBar>
  );
}
